import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Base from '../Base';
import Card from '../../components/elements/Card';
import Title from '../../components/common/Title';
import Back from '../../components/common/Back';
import Icon from '../../components/common/Icon';
import { apiRequest } from '../../utils';
import './CompanyHistory.css';

/* Where we came from — the company's KPI history, drawn.

   ★★ Why this screen can exist at all. The admin home's tiles say where the company STANDS, and
   that is the one thing the database can always answer. Where it STOOD is a different question:
   house points carry no date, the engagement score is rewritten every night, the journey
   percentage is computed live. None of it can be reconstructed, which is why a nightly pass
   writes ``data.CompanyKpiSnapshot`` — and this is the first screen that reads it back.

   GET /api/bravos/company-history?granularity=day|week|month returns:
     buckets   every bucket label in the window, oldest first — including the empty ones
     series    {kpi: [value | null, ...]} ALIGNED to that list
     groups    [{key, kpis}] — the catalogue, owned by the server (labels are ours)
     latest    {kpi: value} as last recorded
     first     the first day ever recorded, for "measuring since"

   ★ ``null`` is not zero and is never drawn as zero. A missing bucket is a night the pass could
   not look; the line BREAKS over it and a dotted connector says so. Drawing it as a plunge to
   the floor would invent a catastrophe out of a server restart, and it is the single most
   likely bug in a screen like this one. */

// The server hands back the bucket calendar per granularity; these are just the three ways of
// asking. Day opens on the last 90 nights, week on a year, month on three.
const GRANULARITIES = ['day', 'week', 'month'];

/* One accent per family, so the hero takes the colour of whatever you tapped and a section can
   be found by its dot alone. Muted on purpose — ten saturated hues on one page is a toy. */
const TONES = {
  people: '#ff784b',
  journey: '#7c3aed',
  purpose: '#d6398f',
  seats: '#0ea5e9',
  recognition: '#f59e0b',
  calendar: '#6366f1',
  questions: '#14b8a6',
  missions: '#ef4444',
  competition: '#8b5cf6',
  houses: '#10b981',
  other: '#64748b',
};
const FALLBACK_TONE = '#64748b';

// What the company opens on: its headline number. Falls through to the first thing there is,
// because a company without a journey still has a history.
const PREFERRED = ['journey_pct', 'engagement_total', 'roster_size'];

/* ---------------------------------------------------------------- geometry

   Everything below works in real pixels rather than a fixed viewBox: the chart is measured with
   a ResizeObserver and drawn at the width it actually has. A 720-wide viewBox scaled into a
   360px phone would take the axis labels down to six point with it, and this is a screen people
   open on a phone. */

const PAD = { top: 16, right: 12, bottom: 24, left: 40 };

/* Consecutive runs of real measurements. The gaps between them are what the dotted connectors
   are drawn across — see the note at the top about why they are not simply joined up. */
const segmentsOf = (values) => {
  const out = [];
  let run = null;
  (values || []).forEach((v, i) => {
    if (v === null || v === undefined) { run = null; return; }
    if (!run) { run = []; out.push(run); }
    run.push(i);
  });
  return out;
};

/* A round number at or above the top of the series, so the axis reads 60 / 45 / 30 / 15 and not
   58.3 / 43.7. Percentages always get the full 0-100: a journey at 52% that fills the frame
   looks finished, which is the opposite of what it says. */
const niceTop = (max, percent) => {
  if (percent) return 100;
  if (max <= 5) return Math.max(1, Math.ceil(max));
  const step = Math.pow(10, Math.floor(Math.log10(max))) / 2;
  return Math.ceil(max / step) * step;
};

/* The floor of the plot. Zero wherever the series comes anywhere near it — a bar-like figure
   read off a cropped axis is how a chart lies — but a level that has never been below 300
   (house points, a roster) gets a padded floor, because thirty points of movement inside a
   0-400 frame is a flat line saying nothing happened. The axis is always labelled with the real
   numbers, so nothing is hidden either way. */
const niceFloor = (min, top) => {
  if (min <= 0 || min < top * 0.35) return 0;
  const step = Math.pow(10, Math.floor(Math.log10(top))) / 2;
  return Math.max(0, Math.floor((min - (top - min) * 0.3) / step) * step);
};

/* Gridlines on round numbers — 30 / 40 / 50 / 60, never 33.75 / 42.5. Four-ish lines, stepped by
   1, 2, 2.5 or 5 times a power of ten, which is what makes an axis readable at a glance instead
   of one to be worked out. */
const tickValues = (floor, top) => {
  const raw = (top - floor) / 4 || 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag * 10;
  const out = [];
  for (let v = Math.ceil(floor / step) * step; v <= top + 1e-9; v += step) out.push(v);
  return out.length ? out : [floor, top];
};

const Trend = ({ buckets, values, color, percent, granularity, lang }) => {
  const { t } = useTranslation();
  const wrapRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      setWidth(Math.round(entries[0].contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const height = width && width < 420 ? 190 : 230;
  const real = (values || []).filter((v) => v !== null && v !== undefined);
  const top = niceTop(real.length ? Math.max(...real) : 1, percent);
  const floor = niceFloor(real.length ? Math.min(...real) : 0, top);
  const plotW = Math.max(1, width - PAD.left - PAD.right);
  const plotH = Math.max(1, height - PAD.top - PAD.bottom);
  const n = (buckets || []).length;
  const x = (i) => PAD.left + (n < 2 ? plotW / 2 : (i / (n - 1)) * plotW);
  const y = (v) => PAD.top + plotH - ((v - floor) / Math.max(1e-9, top - floor)) * plotH;

  const segments = useMemo(() => segmentsOf(values), [values]);
  const ticks = tickValues(floor, top);

  const fmtDate = useCallback((iso) => {
    const d = new Date(`${iso}T00:00:00`);
    if (granularity === 'month') {
      return d.toLocaleDateString(lang, { month: 'short', year: '2-digit' });
    }
    return d.toLocaleDateString(lang, { day: 'numeric', month: 'short' });
  }, [granularity, lang]);

  // Four or five labels along the bottom, never one per point: a daily chart of ninety nights
  // would otherwise print ninety dates on top of each other.
  const labelAt = useMemo(() => {
    if (n <= 1) return new Set([0]);
    const want = Math.min(5, n);
    const set = new Set();
    for (let i = 0; i < want; i += 1) set.add(Math.round((i / (want - 1)) * (n - 1)));
    return set;
  }, [n]);

  const onMove = (e) => {
    if (!width || n < 1) return;
    const box = e.currentTarget.getBoundingClientRect();
    const px = (e.touches ? e.touches[0].clientX : e.clientX) - box.left;
    const i = Math.max(0, Math.min(n - 1, Math.round(((px - PAD.left) / plotW) * (n - 1))));
    setHover(i);
  };

  const gradId = `hist-grad-${color.replace('#', '')}`;
  const hoverValue = hover === null ? null : values[hover];
  const lastIndex = segments.length ? segments[segments.length - 1].slice(-1)[0] : null;

  return (
    <div className="hist-chart" ref={wrapRef}>
      {width > 0 && (
        <svg
          className="hist-chart__svg"
          width={width}
          height={height}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          onTouchStart={onMove}
          onTouchMove={onMove}
          onTouchEnd={() => setHover(null)}
        >
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.30" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient>
          </defs>

          {ticks.map((v, i) => (
            <g key={i}>
              <line className="hist-grid" x1={PAD.left} x2={width - PAD.right}
                    y1={y(v)} y2={y(v)} />
              <text className="hist-axis" x={PAD.left - 6} y={y(v) + 3} textAnchor="end">
                {percent ? `${Math.round(v)}%` : Math.round(v).toLocaleString(lang)}
              </text>
            </g>
          ))}

          {/* The area first, per segment, so a gap is a gap in the fill too. */}
          {segments.map((seg, i) => (
            <path
              key={`a${i}`}
              className="hist-area"
              fill={`url(#${gradId})`}
              d={`M${x(seg[0])},${y(values[seg[0]])}`
                + seg.slice(1).map((j) => `L${x(j)},${y(values[j])}`).join('')
                + `L${x(seg[seg.length - 1])},${PAD.top + plotH}L${x(seg[0])},${PAD.top + plotH}Z`}
            />
          ))}

          {/* ★ The dotted stretch between two segments. It says "we did not look" out loud
              instead of quietly joining Friday to Tuesday as though nothing were missing. */}
          {segments.slice(1).map((seg, i) => {
            const prev = segments[i][segments[i].length - 1];
            return (
              <line key={`g${i}`} className="hist-gap" stroke={color}
                    x1={x(prev)} y1={y(values[prev])} x2={x(seg[0])} y2={y(values[seg[0]])} />
            );
          })}

          {segments.map((seg, i) => (
            <path
              key={`l${i}`}
              className="hist-line"
              stroke={color}
              pathLength="1"
              d={`M${x(seg[0])},${y(values[seg[0]])}`
                + seg.slice(1).map((j) => `L${x(j)},${y(values[j])}`).join('')}
            />
          ))}

          {/* Where we are now: a dot with a soft ring, so the eye lands on the newest figure. */}
          {lastIndex !== null && (
            <g>
              <circle className="hist-now__halo" cx={x(lastIndex)} cy={y(values[lastIndex])}
                      r="9" fill={color} />
              <circle className="hist-now" cx={x(lastIndex)} cy={y(values[lastIndex])}
                      r="3.6" fill={color} />
            </g>
          )}

          {hover !== null && (
            <g>
              <line className="hist-cross" x1={x(hover)} x2={x(hover)}
                    y1={PAD.top} y2={PAD.top + plotH} />
              {hoverValue !== null && hoverValue !== undefined && (
                <circle cx={x(hover)} cy={y(hoverValue)} r="4.5" fill="#fff"
                        stroke={color} strokeWidth="2.5" />
              )}
            </g>
          )}

          {(buckets || []).map((b, i) => (labelAt.has(i) ? (
            <text key={b} className="hist-axis" x={x(i)} y={height - 6}
                  textAnchor={i === 0 ? 'start' : (i === n - 1 ? 'end' : 'middle')}>
              {fmtDate(b)}
            </text>
          ) : null))}
        </svg>
      )}

      {hover !== null && width > 0 && (
        <div
          className="hist-tip"
          style={{ left: Math.max(4, Math.min(width - 116, x(hover) - 58)) }}
        >
          <span className="hist-tip__date">{fmtDate(buckets[hover])}</span>
          <span className="hist-tip__val">
            {hoverValue === null || hoverValue === undefined
              ? t('chNoMeasurement')
              : (percent ? `${hoverValue}%` : hoverValue.toLocaleString(lang))}
          </span>
        </div>
      )}
    </div>
  );
};

/* A number and its shape, small enough that a family of six fits on a phone. The whole card is
   the button: tapping it draws that number big. */
const SparkCard = ({ label, values, color, percent, selected, lang, onPick }) => {
  const real = (values || []).filter((v) => v !== null && v !== undefined);
  const now = real.length ? real[real.length - 1] : null;
  const then = real.length ? real[0] : null;
  const delta = now === null || then === null ? 0 : now - then;
  // ★ The spark's floor is the series' OWN low, not zero. These are levels — a roster between
  // 34 and 58 drawn from a zero baseline is a straight line saying nothing moved, when what it
  // is meant to show is exactly that it did. The big chart, which has an axis to explain
  // itself, is the one that starts at zero wherever it honestly can.
  const top = real.length ? Math.max(...real) : 1;
  const low = real.length ? Math.min(...real) : 0;
  const W = 100;
  const H = 26;
  const n = (values || []).length;
  const px = (i) => (n < 2 ? W / 2 : (i / (n - 1)) * W);
  const py = (v) => (top === low ? H / 2
    : H - 2 - ((v - low) / (top - low)) * (H - 4));

  return (
    <button type="button" className={`hist-card${selected ? ' hist-card--on' : ''}`} onClick={onPick}
            style={selected ? { borderColor: color } : undefined}>
      <span className="hist-card__label">{label}</span>
      <span className="hist-card__row">
        <span className="hist-card__num" style={{ color }}>
          {now === null ? '—' : (percent ? `${now}%` : now.toLocaleString(lang))}
        </span>
        {delta !== 0 && (
          <span className={`hist-chip hist-chip--${delta > 0 ? 'up' : 'down'}`}>
            {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toLocaleString(lang)}
          </span>
        )}
      </span>
      <svg className="hist-card__spark" width="100%" height={H} viewBox={`0 0 ${W} ${H}`}
           preserveAspectRatio="none" aria-hidden="true">
        {segmentsOf(values).map((seg, i) => (
          <path key={i} fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round"
                strokeLinejoin="round" vectorEffect="non-scaling-stroke"
                d={`M${px(seg[0])},${py(values[seg[0]])}`
                  + seg.slice(1).map((j) => `L${px(j)},${py(values[j])}`).join('')} />
        ))}
      </svg>
    </button>
  );
};

const CompanyHistory = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language || 'en';
  const [gran, setGran] = useState('week');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiRequest('GET', `/bravos/company-history?granularity=${gran}`);
      setData(res);
    } catch {
      // Not a contact person of an engagement company — the same bounce every admin-only
      // screen in this track makes.
      navigate('/member-home');
    } finally {
      setLoading(false);
    }
  }, [gran, navigate]);

  useEffect(() => { load(); }, [load]);

  // Memoised because `toneOf` and `available` hang off them: a fresh [] every render would
  // rebuild both on every keystroke of the chart's hover state.
  const groups = useMemo(() => (data && data.groups) || [], [data]);
  const series = (data && data.series) || {};
  const percent = useMemo(() => new Set((data && data.percent) || []), [data]);

  // Which family a KPI belongs to, so its card, its dot and the hero all take one colour.
  const toneOf = useCallback((kpi) => {
    const group = groups.find((g) => g.kpis.includes(kpi));
    return (group && TONES[group.key]) || FALLBACK_TONE;
  }, [groups]);

  const available = useMemo(() => groups.flatMap((g) => g.kpis), [groups]);
  const selected = (picked && available.includes(picked))
    ? picked
    : (PREFERRED.find((k) => available.includes(k)) || available[0] || null);

  const values = selected ? (series[selected] || []) : [];
  const real = values.filter((v) => v !== null && v !== undefined);
  const now = real.length ? real[real.length - 1] : null;
  const then = real.length ? real[0] : null;
  const delta = now === null || then === null ? 0 : now - then;
  const isPct = selected ? percent.has(selected) : false;
  const fmt = (v) => (v === null || v === undefined
    ? '—' : (isPct ? `${v}%` : v.toLocaleString(lang)));

  const pick = (kpi) => {
    setPicked(kpi);
    // The hero is at the top and the families run well below it; without this, tapping a card
    // changes a chart nobody can see.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const since = data && data.first
    ? new Date(`${data.first}T00:00:00`).toLocaleDateString(lang,
      { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  return (
    <Base activeNav="home">
      <Card>
        <Back to="/member-home" />
        <Title my="1" className="mt-3">
          <Icon name="chart-line-up" theme="primary" className="me-2" />
          {t('chTitle')}
        </Title>
        <p className="text-muted text-start hist-intro">{t('chIntro')}</p>

        {loading && !data ? (
          <span className="auth__spinner" />
        ) : !available.length ? (
          /* Nothing has ever been written down. Not an error — the pass runs tonight. */
          <div className="hist-empty">
            <Icon name="time-past" className="hist-empty__icon" />
            <p className="hist-empty__h">{t('chEmptyTitle')}</p>
            <p className="hist-empty__b">{t('chEmptyBody')}</p>
          </div>
        ) : (
          <>
            <div className="hist-controls">
              {GRANULARITIES.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={`hist-pill${gran === g ? ' hist-pill--on' : ''}`}
                  onClick={() => setGran(g)}
                >
                  {t(`chGran_${g}`)}
                </button>
              ))}
              {since && <span className="hist-since">{t('chSince', { date: since })}</span>}
            </div>

            {(data.buckets || []).length < 2 ? (
              /* One night recorded. A point is not a line, and pretending otherwise draws a
                 chart that says nothing while looking like it says something. */
              <div className="hist-empty">
                <Icon name="time-past" className="hist-empty__icon" />
                <p className="hist-empty__h">{t('chOneTitle')}</p>
                <p className="hist-empty__b">{t('chOneBody', { date: since })}</p>
              </div>
            ) : (
              <div className="hist-hero" style={{ '--hist-tone': toneOf(selected) }}>
                <div className="hist-hero__head">
                  <span className="hist-hero__label">{t(`kpi_${selected}`, selected)}</span>
                  <span className="hist-hero__figures">
                    <strong className="hist-hero__num">{fmt(now)}</strong>
                    {delta !== 0 && (
                      <span className={`hist-chip hist-chip--${delta > 0 ? 'up' : 'down'}`}>
                        {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toLocaleString(lang)}
                        {isPct ? ` ${t('chPts')}` : ''}
                      </span>
                    )}
                  </span>
                  {/* ``n`` rather than i18next's ``count``: a count triggers plural-key
                      resolution (chWindow_week_one / _other), and this catalogue is flat. */}
                  <span className="hist-hero__window">
                    {t(`chWindow_${gran}`, { n: (data.buckets || []).length })}
                  </span>
                </div>

                <Trend
                  key={`${selected}-${gran}`}
                  buckets={data.buckets}
                  values={values}
                  color={toneOf(selected)}
                  percent={isPct}
                  granularity={gran}
                  lang={lang}
                />

                <div className="hist-rail">
                  <span className="hist-rail__cell">
                    <span className="hist-rail__k">{t('chLow')}</span>
                    <span className="hist-rail__v">{fmt(real.length ? Math.min(...real) : null)}</span>
                  </span>
                  <span className="hist-rail__cell">
                    <span className="hist-rail__k">{t('chHigh')}</span>
                    <span className="hist-rail__v">{fmt(real.length ? Math.max(...real) : null)}</span>
                  </span>
                  <span className="hist-rail__cell">
                    <span className="hist-rail__k">{t('chThen')}</span>
                    <span className="hist-rail__v">{fmt(then)}</span>
                  </span>
                  <span className="hist-rail__cell">
                    <span className="hist-rail__k">{t('chNow')}</span>
                    <span className="hist-rail__v">{fmt(now)}</span>
                  </span>
                </div>

                {/* Only said when there is actually a break in the window being looked at —
                    an explanation of something that is not on the screen is just noise. */}
                {segmentsOf(values).length > 1 && (
                  <p className="hist-gapnote">
                    <Icon name="interrogation" className="me-1" />{t('chGapNote')}
                  </p>
                )}
              </div>
            )}

            <p className="hist-hint">{t('chPickHint')}</p>

            {groups.map((g) => (
              <section className="hist-group" key={g.key}>
                <p className="hist-group__h">
                  <span className="hist-group__dot"
                        style={{ background: TONES[g.key] || FALLBACK_TONE }} />
                  {t(`chGroup_${g.key}`, g.key)}
                </p>
                <div className="hist-cards">
                  {g.kpis.map((kpi) => (
                    <SparkCard
                      key={kpi}
                      label={t(`kpi_${kpi}`, kpi)}
                      values={series[kpi] || []}
                      color={TONES[g.key] || FALLBACK_TONE}
                      percent={percent.has(kpi)}
                      selected={kpi === selected}
                      lang={lang}
                      onPick={() => pick(kpi)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )}
      </Card>
    </Base>
  );
};

export default CompanyHistory;
